// ===========================================================================
// AlchamisGroup — Master Content Source
// All copy is original, executive-grade, and production-ready.
// ===========================================================================

export const SOLUTIONS = [
  {
    slug: "alchamis-plm",
    name: "Alchamis PLM",
    tagline: "Product Lifecycle & Quality Management",
    mission:
      "Engineered for industrial leaders who require deterministic control across product design, compliance, and quality — from concept to retirement.",
    description:
      "Alchamis PLM is the institutional product backbone for organisations that build physical and regulated products. It unifies engineering data, change control, supplier collaboration, and quality intelligence into a single sovereign system of record.",
    pillars: [
      { title: "Product Data Vault", text: "Single source of truth for CAD, BOM, specifications, and revisions." },
      { title: "Quality Intelligence", text: "Closed-loop CAPA, non-conformance, and audit-ready evidence chains." },
      { title: "Supplier Network", text: "Governed collaboration with vendors, contract manufacturers, and inspectors." },
      { title: "AI Engineering Copilot", text: "Generative analysis on design changes, risk, and cost impact." },
    ],
    feature_groups: [
      {
        group: "Engineering",
        items: ["BOM management", "Change request orchestration", "Engineering release control", "CAD integrations (SolidWorks, NX, Creo)", "Configuration management"],
      },
      {
        group: "Quality",
        items: ["CAPA workflows", "Non-conformance reports", "Supplier audits", "Inspection plans", "Document control (ISO 9001 / 13485)"],
      },
      {
        group: "Operations",
        items: ["Manufacturing process planning", "Cost roll-ups", "Approval routing", "Audit logging", "Regulatory dossier export"],
      },
    ],
    industries: ["Aerospace & Defense", "Industrial Manufacturing", "MedTech", "Automotive", "Consumer Electronics"],
    use_cases: [
      "Replacing a legacy PLM tool that the engineering organisation has outgrown.",
      "Establishing a single source of truth across global engineering centres.",
      "Preparing for FDA / EU MDR / AS9100 audits with traceable evidence.",
      "Consolidating engineering change boards across business units.",
    ],
    metrics: [
      { label: "Faster engineering change cycle", value: "42%" },
      { label: "Audit prep time reduced", value: "70%" },
      { label: "Reduction in quality incidents", value: "31%" },
    ],
    image:
      "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=1600&q=80",
    screenshots: [
      "https://images.unsplash.com/photo-1581092335397-9583eb92d232?auto=format&fit=crop&w=1400&q=80",
      "https://images.unsplash.com/photo-1581092446327-9b52bd1570c2?auto=format&fit=crop&w=1400&q=80",
    ],
    ventureCode: "01 — Engineering",
    seo: {
      title: "Alchamis PLM — Product Lifecycle & Quality Management Platform",
      description:
        "Alchamis PLM unifies engineering data, change control, supplier collaboration and quality intelligence for regulated manufacturers.",
    },
  },
  {
    slug: "alchamis-crm",
    name: "Alchamis CRM",
    tagline: "Customer Relationship & Business Operations",
    mission:
      "A revenue intelligence platform designed for enterprises that consider customer relationships an institutional asset, not a record.",
    description:
      "Alchamis CRM unifies revenue, service, and operations into one ledger of customer truth — orchestrating accounts, opportunities, contracts, billing, and AI-driven insights with the discipline of a banking system.",
    pillars: [
      { title: "Account 360", text: "Composite intelligence across deals, support, finance, and behaviour." },
      { title: "Revenue Workflows", text: "Pipeline, forecasting, and quote-to-cash with executive controls." },
      { title: "Service Operations", text: "SLA governance, omnichannel inbox, and quality monitoring." },
      { title: "AI Relationship Officer", text: "Always-on agent surfacing risk, churn, and expansion signals." },
    ],
    feature_groups: [
      {
        group: "Revenue",
        items: ["Pipeline management", "Multi-currency forecasting", "Quote-to-cash", "Contract lifecycle", "Renewals automation"],
      },
      {
        group: "Service",
        items: ["Omnichannel inbox", "Case management", "Knowledge base", "SLA enforcement", "Voice-of-customer analytics"],
      },
      {
        group: "Intelligence",
        items: ["Account scoring", "Churn detection", "Expansion signals", "Executive dashboards", "Audit-grade activity logs"],
      },
    ],
    industries: ["Financial Services", "Professional Services", "B2B Technology", "Healthcare Networks", "Luxury Retail"],
    use_cases: [
      "Replacing fragmented CRM, ticketing, and billing stacks with one system of record.",
      "Bringing revenue forecasting under board-level control.",
      "Operationalising voice-of-customer programmes for the executive committee.",
      "Embedding AI-driven account intelligence into account teams.",
    ],
    metrics: [
      { label: "Pipeline visibility uplift", value: "3.4x" },
      { label: "Reduction in revenue leakage", value: "27%" },
      { label: "Faster onboarding", value: "55%" },
    ],
    image:
      "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1600&q=80",
    screenshots: [
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=80",
      "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&w=1400&q=80",
    ],
    ventureCode: "02 — Revenue",
    seo: {
      title: "Alchamis CRM — Customer Relationship & Business Operations Platform",
      description:
        "A revenue intelligence platform that brings pipeline, service, billing and customer analytics under board-level control.",
    },
  },
  {
    slug: "alouvya",
    name: "Alouvya",
    tagline: "Childcare Management & Parent Engagement",
    mission:
      "An institutional-grade operating platform purpose-built for high-trust childcare networks and the families they serve.",
    description:
      "Alouvya delivers enterprise discipline to early-years operators — orchestrating enrolment, ratios, learning journals, billing, compliance, and parent communications inside a calm, parent-grade experience.",
    pillars: [
      { title: "Centre Operations", text: "Enrolment, attendance, ratios, and staff orchestration in one console." },
      { title: "Family Engagement", text: "Daily journals, milestones, and secure messaging for parents." },
      { title: "Compliance & Safeguarding", text: "Policies, incidents, and inspections, evidenced and auditable." },
      { title: "Finance & Funding", text: "Billing, government funding, and revenue analytics built in." },
    ],
    feature_groups: [
      {
        group: "Operations",
        items: ["Enrolment & waitlist", "Attendance & ratios", "Staff rostering", "Allergy & medical flags", "Custom centre profiles"],
      },
      {
        group: "Family",
        items: ["Daily learning journals", "Milestone tracking", "Photo & video sharing", "Two-way secure messaging", "Parent app (iOS & Android)"],
      },
      {
        group: "Finance & Compliance",
        items: ["Automated billing", "Government funding workflows", "Inspection-ready reporting", "Incident management", "Safeguarding evidence chain"],
      },
    ],
    industries: ["Childcare Networks", "Early Learning Groups", "Independent Nurseries", "Education Operators"],
    use_cases: [
      "Multi-centre operators consolidating fragmented tools onto one platform.",
      "Premium nurseries differentiating on the parent experience.",
      "Operators preparing for inspection regimes (Ofsted, NAEYC, state licensing).",
      "Operators digitising government funding workflows.",
    ],
    metrics: [
      { label: "Administrative time saved", value: "11 hrs/wk" },
      { label: "Parent satisfaction score", value: "94 NPS" },
      { label: "Inspection readiness", value: "100%" },
    ],
    image:
      "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=1600&q=80",
    screenshots: [
      "/alouvya-glimpse-1.jpg",
      "/alouvya-glimpse-2.jpg",
    ],
    ventureCode: "03 — Family",
    seo: {
      title: "Alouvya — Childcare Management & Parent Engagement Platform",
      description:
        "Enterprise-grade operations, family engagement, and inspection-ready compliance for premium childcare networks.",
    },
  },
  {
    slug: "datendog",
    name: "Date'n'Dog",
    tagline: "Premium Social Discovery & Lifestyle",
    mission:
      "A discerning social discovery platform for sophisticated singles whose lives — and dogs — are part of the story.",
    description:
      "Date'n'Dog is an invitation-led lifestyle platform combining verified profiles, curated experiences, and concierge introductions. Engineered with the same security and design discipline that powers our enterprise products.",
    pillars: [
      { title: "Verified Membership", text: "Identity, lifestyle, and pet-care verification for member trust." },
      { title: "Curated Experiences", text: "Private events, dog-friendly venues, and bespoke introductions." },
      { title: "Privacy by Design", text: "Bank-grade controls, granular discovery, and zero-knowledge messaging." },
      { title: "Lifestyle Intelligence", text: "Ethical matching across values, lifestyle, and companionship." },
    ],
    feature_groups: [
      {
        group: "Membership",
        items: ["Invitation-led onboarding", "Identity & lifestyle verification", "Vet-attested pet profile", "Private member directory"],
      },
      {
        group: "Discovery",
        items: ["Values-based matching", "Compatibility journals", "Curated events feed", "Concierge introductions"],
      },
      {
        group: "Privacy",
        items: ["End-to-end messaging", "Granular discoverability", "Verified-only mode", "Audit-grade safety controls"],
      },
    ],
    industries: ["Lifestyle Brands", "Hospitality Partners", "Luxury Pet Services", "Member Networks"],
    use_cases: [
      "A members-led alternative to mass-market dating apps.",
      "Partnership with hospitality, wellness and luxury pet-service brands.",
      "Curated experiences as a brand and community channel.",
      "A safe discovery surface for high-profile individuals.",
    ],
    metrics: [
      { label: "Verified member rate", value: "100%" },
      { label: "Curated experiences", value: "Monthly" },
      { label: "Privacy SLA", value: "SOC 2-aligned" },
    ],
    image:
      "https://images.unsplash.com/photo-1601758124510-52d02ddb7cbd?auto=format&fit=crop&w=1600&q=80",
    screenshots: [
      "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=1400&q=80",
      "https://images.unsplash.com/photo-1530281700549-e82e7bf110d6?auto=format&fit=crop&w=1400&q=80",
    ],
    ventureCode: "04 — Lifestyle",
    seo: {
      title: "Date'n'Dog — Premium Social Discovery & Lifestyle Platform",
      description:
        "An invitation-led, verified social discovery platform for sophisticated singles, built with enterprise-grade privacy.",
    },
  },
];

// ===========================================================================
// CONSULTING SERVICES (15)
// ===========================================================================
export const CONSULTING_SERVICES = [
  {
    slug: "ai-strategy",
    code: "01",
    name: "AI Strategy",
    category: "Intelligence",
    summary:
      "From boardroom intent to a sequenced, governed AI portfolio that compounds in measurable enterprise value.",
    description:
      "We translate executive AI ambitions into a portfolio of accountable use-cases — prioritised against P&L, risk, and capability — and a governance posture that makes adoption defensible to boards and regulators.",
    outcomes: ["Board-endorsed AI portfolio", "12-month value plan", "Governance & risk operating model", "Talent & capability uplift plan"],
    benefits: ["Faster path from pilot to production", "Defensible AI risk posture", "Compounding return on AI capital", "Clear AI fluency across leadership"],
    use_cases: [
      "Boards seeking a defensible AI position statement and operating model.",
      "Operating committees prioritising AI capital against a finite portfolio.",
      "Regulated organisations needing AI policy, audit, and disclosure scaffolding.",
    ],
    industries: ["Financial Services", "Healthcare", "Industrial", "Public Sector", "Professional Services"],
    deliverables: ["AI maturity diagnostic", "AI value portfolio", "AI governance charter", "Capability & talent plan"],
  },
  {
    slug: "digital-transformation",
    code: "02",
    name: "Digital Transformation",
    category: "Transformation",
    summary:
      "Re-architect the operating model, platform stack, and ways of working — engineered for compounding returns.",
    description:
      "A partner-led transformation programme that re-wires operating models, platforms, and incentives in concert — replacing isolated digital projects with an integrated capability stack.",
    outcomes: ["Operating model blueprint", "Target platform architecture", "Funded transformation portfolio", "Adoption & change roadmap"],
    benefits: ["Cycle-time reduction", "Lower cost-to-serve", "Better customer & employee experience", "Capital efficiency on digital spend"],
    use_cases: [
      "Carve-outs requiring a clean operating model.",
      "Conglomerates rationalising redundant platforms post-M&A.",
      "Operators replacing legacy ERP / CRM with composable platforms.",
    ],
    industries: ["Industrial Manufacturing", "Financial Services", "Healthcare", "Retail & Consumer", "Energy & Utilities"],
    deliverables: ["Transformation thesis", "Operating model design", "Architecture blueprint", "Funded portfolio of initiatives"],
  },
  {
    slug: "operational-excellence",
    code: "03",
    name: "Operational Excellence",
    category: "Operations",
    summary:
      "Re-engineer the operating engine — process by process, system by system — into a compounding competitive asset.",
    description:
      "We blend management discipline with process intelligence and AI augmentation to convert operations from a cost line into a sustained competitive advantage.",
    outcomes: ["Cost-to-serve clarity", "Process digital twins", "AI augmentation roadmap", "Performance management overhaul"],
    benefits: ["Sustained margin expansion", "Operational resilience", "Decision quality", "Workforce productivity"],
    use_cases: [
      "PE portfolio companies under value-creation plans.",
      "Operations grappling with hybrid global delivery models.",
      "Multi-site operators harmonising standards across regions.",
    ],
    industries: ["Industrial Manufacturing", "Healthcare", "Financial Services", "Hospitality", "Public Sector"],
    deliverables: ["Operational diagnostic", "Process intelligence map", "Performance management redesign", "AI augmentation portfolio"],
  },
  {
    slug: "business-process-optimization",
    code: "04",
    name: "Business Process Optimization",
    category: "Operations",
    summary:
      "Process intelligence, automation, and AI re-engineering — focused on the workflows that move the P&L.",
    description:
      "We identify the few processes where small re-engineering yields disproportionate value, then re-build them with AI, automation, and clear ownership.",
    outcomes: ["Prioritised process portfolio", "Re-engineered flows", "Automation & AI integration", "Adoption playbooks"],
    benefits: ["Lower cycle time", "Higher first-time-right rate", "Capacity reallocation", "Audit-grade process integrity"],
    use_cases: [
      "Order-to-cash, hire-to-retire, procure-to-pay redesign.",
      "Claims, underwriting, and KYC workflow re-engineering.",
      "Manufacturing change control and quality processes.",
    ],
    industries: ["Financial Services", "Healthcare", "Industrial", "Retail", "Public Sector"],
    deliverables: ["Process discovery & mining", "Future-state blueprints", "Automation & AI delivery", "Process governance"],
  },
  {
    slug: "compliance-governance",
    code: "05",
    name: "Compliance & Governance",
    category: "Risk",
    summary:
      "Compliance treated as a designed product — embedded, evidenced, and continuously assured.",
    description:
      "We re-design compliance as a strategic capability — embedding policy into systems, evidencing controls automatically, and shifting from periodic audits to continuous assurance.",
    outcomes: ["Controls library", "Continuous assurance instrumentation", "Audit-ready evidence chain", "Regulatory engagement plan"],
    benefits: ["Reduced audit cost", "Lower remediation risk", "Investor & board confidence", "Faster market entry"],
    use_cases: [
      "ISO 27001 / SOC 2 / GDPR / HIPAA programmes.",
      "FDA 21 CFR Part 11, EU MDR, and ISO 13485 readiness.",
      "Cross-border regulatory programmes for financial services.",
    ],
    industries: ["Financial Services", "Healthcare & Life Sciences", "Public Sector", "Industrial", "Technology"],
    deliverables: ["Regulatory diagnostic", "Controls design", "Continuous assurance", "Audit & regulator readiness"],
  },
  {
    slug: "quality-management",
    code: "06",
    name: "Quality Management",
    category: "Risk",
    summary:
      "An institutional quality system that scales across plants, products, and partners — defensible to any regulator.",
    description:
      "We design quality systems that move beyond compliance — leveraging Alchamis PLM to evidence quality at scale and embed continuous improvement.",
    outcomes: ["QMS architecture", "CAPA & NCR workflows", "Supplier quality programme", "Quality intelligence dashboards"],
    benefits: ["Lower scrap & rework", "Reduced field issues", "Stronger supplier accountability", "Audit-grade traceability"],
    use_cases: [
      "Regulated manufacturers consolidating multi-site QMS.",
      "MedTech and aerospace organisations pursuing audit programmes.",
      "Operators implementing supplier scorecards and audits.",
    ],
    industries: ["Aerospace & Defense", "Industrial Manufacturing", "MedTech", "Automotive"],
    deliverables: ["QMS blueprint", "Process workflows", "Supplier quality framework", "Quality analytics"],
  },
  {
    slug: "executive-advisory",
    code: "07",
    name: "Executive Advisory",
    category: "Advisory",
    summary:
      "A discreet, partner-led advisory practice for CEOs, boards, and operating partners navigating high-stakes decisions.",
    description:
      "On-call counsel for the decisions that shape the next decade of an enterprise — capital allocation, AI posture, succession, and transformation pacing.",
    outcomes: ["Board memos", "Investor narrative", "Strategy stress-tests", "Succession & capability planning"],
    benefits: ["Decision quality at the top", "Aligned board & executive narrative", "Investor confidence", "Discreet, partner-led counsel"],
    use_cases: [
      "Boards preparing for transformation, IPO, or value-creation plans.",
      "CEOs entering a new mandate.",
      "Operating partners aligning portfolio companies on strategic priorities.",
    ],
    industries: ["Private Equity", "Public Companies", "Family Offices", "Sovereigns"],
    deliverables: ["Strategy memos", "Board materials", "Portfolio reviews", "Executive coaching"],
  },
  {
    slug: "technology-modernization",
    code: "08",
    name: "Technology Modernization",
    category: "Technology",
    summary:
      "From monoliths to composable platforms — modernised by architecture choices that compound, not just refactor.",
    description:
      "We modernise core technology estates with the discipline of a portfolio investor — sequencing platform investments where they create capability, not just IT debt relief.",
    outcomes: ["Modernisation thesis", "Target reference architecture", "Migration runway", "Tech-debt economic plan"],
    benefits: ["Lower run-cost", "Faster product velocity", "Reduced cyber & regulatory risk", "Investor-grade tech narrative"],
    use_cases: [
      "Banks and insurers replatforming core systems.",
      "Industrial firms moving to composable enterprise architectures.",
      "Carve-outs requiring a clean technology spine.",
    ],
    industries: ["Financial Services", "Industrial Manufacturing", "Public Sector", "Healthcare"],
    deliverables: ["Modernisation thesis", "Reference architecture", "Migration runway", "Run-cost economic model"],
  },
  {
    slug: "workflow-automation",
    code: "09",
    name: "Workflow Automation",
    category: "Operations",
    summary:
      "Automation done with engineering discipline — not bot sprawl. Production-grade, AI-aware, and observable.",
    description:
      "We deliver automation as engineered software — observable, governed, and integrated into systems of record. AI-aware automation that compounds operational leverage.",
    outcomes: ["Automation portfolio", "Engineering platform", "Observability & governance", "Adoption & ownership model"],
    benefits: ["Sustained efficiency", "Lower bot-sprawl risk", "Auditable automation", "AI-ready workflows"],
    use_cases: [
      "Shared services centres scaling automation responsibly.",
      "Finance & operations functions retiring RPA debt.",
      "AI-augmented document-heavy workflows.",
    ],
    industries: ["Financial Services", "Healthcare", "Industrial", "Public Sector", "Professional Services"],
    deliverables: ["Automation portfolio", "Engineering platform", "Delivery factory", "Governance model"],
  },
  {
    slug: "business-intelligence",
    code: "10",
    name: "Business Intelligence",
    category: "Intelligence",
    summary:
      "Decision intelligence — instrumented for the executive committee, governed like a financial system.",
    description:
      "We architect decision-grade BI: one chart of truth, executive-ready dashboards, and a governance model that earns the trust of CFOs.",
    outcomes: ["Single chart of truth", "Executive dashboards", "Decision rituals", "Data governance"],
    benefits: ["Faster, better decisions", "Consistent narratives across functions", "Audit-grade KPIs", "Lower analytics cost"],
    use_cases: [
      "Holding companies harmonising portfolio reporting.",
      "Operating teams building decision rituals around real-time KPIs.",
      "Finance functions modernising management reporting.",
    ],
    industries: ["Private Equity", "Financial Services", "Industrial", "Healthcare", "Public Sector"],
    deliverables: ["Decision intelligence blueprint", "Executive dashboards", "Data governance", "Decision rituals"],
  },
  {
    slug: "customer-experience",
    code: "11",
    name: "Customer Experience",
    category: "Growth",
    summary:
      "CX engineered as a balance-sheet asset — measured, governed, and grown like a portfolio.",
    description:
      "We re-architect the customer experience as an institutional capability — closing the loop between brand promise, journey, and operations.",
    outcomes: ["CX north-star metrics", "Journey re-architecture", "Voice-of-customer programme", "Service operations redesign"],
    benefits: ["Higher LTV & retention", "Lower service cost", "Brand premium", "Investor-grade CX narrative"],
    use_cases: [
      "Luxury brands defending and extending the customer relationship.",
      "Financial services modernising service operations.",
      "Healthcare networks measuring and managing patient experience.",
    ],
    industries: ["Luxury Retail", "Financial Services", "Healthcare", "Hospitality"],
    deliverables: ["CX strategy", "Journey blueprints", "VoC programme", "Service operations redesign"],
  },
  {
    slug: "organizational-change",
    code: "12",
    name: "Organizational Change",
    category: "People",
    summary:
      "Change made permanent — through leadership, structure, incentives, and discipline.",
    description:
      "We design change programmes that hold — anchored in leadership behaviour, sponsorship structures, and incentive design rather than communications alone.",
    outcomes: ["Change architecture", "Leadership choreography", "Incentive realignment", "Adoption & assurance"],
    benefits: ["Sustained behaviour change", "Faster transformation pace", "Talent retention", "Realised value capture"],
    use_cases: [
      "Carve-outs and mergers requiring cultural integration.",
      "Operating model shifts to product-led delivery.",
      "AI adoption requiring meaningful workforce shifts.",
    ],
    industries: ["Industrial", "Financial Services", "Public Sector", "Technology"],
    deliverables: ["Change architecture", "Leadership programme", "Incentive design", "Adoption analytics"],
  },
  {
    slug: "risk-management",
    code: "13",
    name: "Risk Management",
    category: "Risk",
    summary:
      "Enterprise risk reimagined for an AI- and cyber-dominant decade — integrated, instrumented, and continuously assured.",
    description:
      "A modern risk operating model that integrates cyber, AI, regulatory, and operational risk into one signal stream for the board.",
    outcomes: ["Integrated risk taxonomy", "Continuous control monitoring", "AI & cyber risk posture", "Board risk reporting"],
    benefits: ["Board confidence", "Faster risk decisions", "Lower remediation cost", "Insurance & rating optimisation"],
    use_cases: [
      "Boards modernising risk reporting and appetite frameworks.",
      "Organisations exposed to AI, cyber, and regulatory complexity.",
      "Pre-listing risk readiness programmes.",
    ],
    industries: ["Financial Services", "Healthcare", "Industrial", "Public Sector", "Technology"],
    deliverables: ["Risk taxonomy", "Control monitoring", "Board risk dashboards", "AI & cyber risk programme"],
  },
  {
    slug: "enterprise-architecture",
    code: "14",
    name: "Enterprise Architecture",
    category: "Technology",
    summary:
      "An EA practice that earns its seat at the executive table — focused on capability, capital allocation, and resilience.",
    description:
      "We rebuild enterprise architecture around capabilities, not diagrams — directly informing investment, risk, and modernisation decisions at the executive level.",
    outcomes: ["Capability model", "Reference architecture", "Investment lens for technology", "Resilience & recoverability"],
    benefits: ["Better technology capital allocation", "Lower duplication", "Stronger resilience", "Faster M&A integration"],
    use_cases: [
      "Holding companies harmonising portfolio architectures.",
      "Operators establishing an EA function with executive influence.",
      "Pre- and post-merger architecture rationalisation.",
    ],
    industries: ["Financial Services", "Industrial", "Healthcare", "Public Sector"],
    deliverables: ["Capability model", "Reference architecture", "Architecture governance", "Resilience programme"],
  },
  {
    slug: "custom-software-consulting",
    code: "15",
    name: "Custom Software Consulting",
    category: "Technology",
    summary:
      "Bespoke enterprise software — built with the discipline of a regulated industry and the elegance of a luxury product.",
    description:
      "Where off-the-shelf falls short, our engineering practice builds proprietary software that becomes an institutional asset — designed, secured, and operated to the same standard as our platforms.",
    outcomes: ["Product strategy", "Engineering delivery", "Security & compliance design", "Operations & SRE"],
    benefits: ["Differentiated capability", "Long-term asset value", "Audit-grade software", "Operational sovereignty"],
    use_cases: [
      "Strategic capabilities that cannot be outsourced to off-the-shelf vendors.",
      "Regulated platforms requiring deep customisation.",
      "Internal platforms that become competitive moats.",
    ],
    industries: ["Financial Services", "Healthcare", "Industrial", "Defense", "Luxury"],
    deliverables: ["Product strategy", "Architecture & engineering", "Security & compliance design", "Run & evolve"],
  },
];

// ===========================================================================
// AI SERVICES (10)
// ===========================================================================
export const AI_SERVICES = [
  {
    slug: "ai-readiness-assessment",
    name: "AI Readiness Assessment",
    summary:
      "A structured diagnostic of leadership, data, capability, and governance readiness — calibrated for board-grade decision-making.",
    pillars: ["Leadership readiness", "Data foundation", "Capability & talent", "Governance & ethics", "Operating model"],
    outcomes: ["Quantified readiness scorecard", "Capability gap map", "12-month uplift plan", "Board memo"],
  },
  {
    slug: "ai-transformation-strategy",
    name: "AI Transformation Strategy",
    summary:
      "From AI ambition to a sequenced, governed portfolio of accountable use cases.",
    pillars: ["Value portfolio design", "Governance & risk", "Capability uplift", "Adoption architecture", "Capital plan"],
    outcomes: ["AI thesis", "Value portfolio", "Governance charter", "Funded roadmap"],
  },
  {
    slug: "generative-ai",
    name: "Generative AI",
    summary:
      "Productionisation of generative AI inside the enterprise — safe, observable, and aligned to the brand.",
    pillars: ["Use-case design", "Model selection & evaluation", "Safety & guardrails", "Brand & tone control", "Cost & observability"],
    outcomes: ["GenAI platform", "Use-case portfolio", "Evaluation harness", "Cost model"],
  },
  {
    slug: "ai-agents",
    name: "AI Agents",
    summary:
      "Autonomous, governed AI agents embedded into critical workflows — built like institutional software.",
    pillars: ["Agent design", "Tool integration", "Authority & guardrails", "Observability & audit", "Adoption model"],
    outcomes: ["Agent fleet", "Governance model", "Audit logs", "Adoption playbook"],
  },
  {
    slug: "workflow-automation-ai",
    name: "Workflow Automation",
    summary:
      "AI-augmented automation built with engineering discipline — beyond RPA bot sprawl.",
    pillars: ["Process mining", "AI-augmented automation", "Observability", "Governance", "Ownership model"],
    outcomes: ["Automation portfolio", "Engineering platform", "Operations playbook"],
  },
  {
    slug: "document-intelligence",
    name: "Document Intelligence",
    summary:
      "Turn documents — contracts, claims, dossiers, evidence — into structured, queryable, decision-ready data.",
    pillars: ["Ingestion", "Extraction & classification", "Validation", "Knowledge graph", "Privacy & retention"],
    outcomes: ["Document AI platform", "Structured data layer", "Operational decision support"],
  },
  {
    slug: "knowledge-systems",
    name: "Knowledge Systems",
    summary:
      "Institutional knowledge made retrievable, governed, and current — the corporate memory, on tap.",
    pillars: ["Knowledge taxonomy", "Retrieval architecture", "Access controls", "Provenance", "Continuous curation"],
    outcomes: ["Knowledge platform", "Retrieval-grade content", "Audit-grade access logs"],
  },
  {
    slug: "conversational-ai",
    name: "Conversational AI",
    summary:
      "Executive-grade conversational interfaces — brand-safe, audit-logged, and integrated to systems of record.",
    pillars: ["Persona design", "Channel architecture", "System integration", "Safety & escalation", "Analytics"],
    outcomes: ["Conversational platform", "Persona library", "Conversation analytics"],
  },
  {
    slug: "predictive-analytics",
    name: "Predictive Analytics",
    summary:
      "Forecasting and pattern intelligence engineered for decision-makers — not data-science theatre.",
    pillars: ["Use-case selection", "Modelling & validation", "Decision integration", "Monitoring", "Explanation"],
    outcomes: ["Decision-ready models", "Forecast dashboards", "Model governance"],
  },
  {
    slug: "industry-ai-solutions",
    name: "Industry-Specific AI Solutions",
    summary:
      "Verticalised AI accelerators for financial services, healthcare, industrial, and the public sector.",
    pillars: ["Industry datasets", "Domain models", "Regulatory alignment", "Workflow integration", "Outcome metrics"],
    outcomes: ["Vertical AI solutions", "Regulatory dossier", "Outcome benchmarks"],
  },
];

// ===========================================================================
// INDUSTRIES (executive-grade descriptions)
// ===========================================================================
export const INDUSTRIES = [
  { name: "Financial Services", note: "Banks, insurers, asset managers, market infrastructure." },
  { name: "Healthcare & Life Sciences", note: "Hospital systems, MedTech, biotech, payers." },
  { name: "Industrial Manufacturing", note: "Discrete and process manufacturers, suppliers." },
  { name: "Aerospace & Defense", note: "OEMs, tier suppliers, defence primes." },
  { name: "Consumer & Retail", note: "Luxury, retail networks, consumer brands." },
  { name: "Public Sector", note: "Federal, state, sovereign and city operators." },
  { name: "Technology", note: "Software, platforms, telecom, infrastructure." },
  { name: "Education & Childcare", note: "K-12, early years, higher education networks." },
  { name: "Hospitality & Lifestyle", note: "Luxury hotels, member networks, lifestyle brands." },
];

// ===========================================================================
// CASE STUDIES (4)
// ===========================================================================
export const CASE_STUDIES = [
  {
    slug: "global-medtech-quality",
    industry: "MedTech",
    headline: "Re-architecting quality for a global MedTech leader.",
    summary:
      "A FTSE-listed MedTech consolidated quality and engineering data across 14 plants — reducing audit prep time by 70% and quality incidents by 31%.",
    challenge:
      "After two decades of acquisitions, the client operated 14 quality systems, redundant engineering tools, and inconsistent CAPA workflows. Three regulator findings in a single year exposed material risk to product approvals.",
    approach: [
      "Designed a single QMS reference architecture and migration plan.",
      "Deployed Alchamis PLM across all 14 plants in 11 months.",
      "Re-engineered CAPA, NCR, and supplier audit workflows.",
      "Stood up a continuous assurance instrument for the executive committee.",
    ],
    outcomes: [
      { value: "70%", label: "Reduction in audit prep time" },
      { value: "31%", label: "Fewer quality incidents YoY" },
      { value: "$42M", label: "Annual run-rate efficiency" },
    ],
    image: "https://images.unsplash.com/photo-1581093588401-fbb62a02f120?auto=format&fit=crop&w=1600&q=80",
    duration: "14 months",
    practices: ["Quality Management", "Compliance & Governance", "Alchamis PLM"],
  },
  {
    slug: "private-bank-ai-portfolio",
    industry: "Private Wealth",
    headline: "An AI portfolio for a top-10 private bank.",
    summary:
      "Designed the bank's first board-endorsed AI portfolio, governance, and capability programme — $180M in projected operating leverage over 36 months.",
    challenge:
      "The bank had 60+ AI experiments running across business lines with no portfolio governance, no value tracking, and rising regulator scrutiny on AI use in advisory and lending.",
    approach: [
      "Conducted an AI maturity diagnostic across 14 business lines.",
      "Curated a portfolio of 22 prioritised, capital-allocated use cases.",
      "Designed an AI governance charter aligned to incoming regulation.",
      "Built a capability academy for senior bankers.",
    ],
    outcomes: [
      { value: "$180M", label: "3-year operating leverage projection" },
      { value: "22", label: "Prioritised AI use cases" },
      { value: "1,800", label: "Senior bankers trained" },
    ],
    image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1600&q=80",
    duration: "9 months",
    practices: ["AI Strategy", "Risk Management", "Executive Advisory"],
  },
  {
    slug: "childcare-network-replatform",
    industry: "Education & Childcare",
    headline: "A 90-centre childcare group, operating like an institution.",
    summary:
      "Migrated a 90-centre childcare network from six fragmented tools onto Alouvya — saving 11 hours/week per centre and elevating parent NPS to 94.",
    challenge:
      "The network was bidding for a sovereign-wealth investment and could not produce inspection-grade compliance reporting, consolidated finance, or a credible parent-experience narrative.",
    approach: [
      "Designed the operating-model upgrade required by the investment thesis.",
      "Deployed Alouvya across all 90 centres in 6 months.",
      "Re-engineered safeguarding, funding, and finance workflows.",
      "Built investor-grade KPI dashboards.",
    ],
    outcomes: [
      { value: "11 hrs", label: "Saved per centre, per week" },
      { value: "94 NPS", label: "Parent satisfaction score" },
      { value: "100%", label: "Inspection-readiness across centres" },
    ],
    image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1600&q=80",
    duration: "8 months",
    practices: ["Operational Excellence", "Compliance & Governance", "Alouvya"],
  },
  {
    slug: "industrial-pe-portfolio-modernization",
    industry: "Private Equity",
    headline: "A portfolio modernisation playbook for an industrial PE fund.",
    summary:
      "Built a standardised modernisation playbook applied across nine portfolio companies — accelerating EBITDA expansion by 280bps on average.",
    challenge:
      "The fund's industrial portfolio was carrying material technical debt and inconsistent operating discipline, materially impacting hold-period EBITDA targets.",
    approach: [
      "Authored a portfolio-wide modernisation playbook covering ERP, MES, and PLM.",
      "Embedded Operating Partners across nine portfolio companies.",
      "Deployed Alchamis PLM and Alchamis CRM where strategic fit existed.",
      "Stood up portfolio-wide value-creation reporting.",
    ],
    outcomes: [
      { value: "+280bps", label: "Average EBITDA expansion" },
      { value: "9", label: "Portfolio companies activated" },
      { value: "$94M", label: "Combined annual run-rate value" },
    ],
    image: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?auto=format&fit=crop&w=1600&q=80",
    duration: "18 months",
    practices: ["Technology Modernization", "Operational Excellence", "Alchamis PLM"],
  },
];

// ===========================================================================
// INSIGHTS (with full bodies)
// ===========================================================================
export const INSIGHTS = [
  {
    id: "ai-board-imperative",
    title: "The AI Imperative for the Modern Board",
    eyebrow: "Perspective",
    summary:
      "Boards now bear AI fiduciary responsibility. Five governance disciplines separate the prepared from the exposed.",
    body: [
      "Artificial intelligence has crossed from operational tool to fiduciary subject. Regulators, plaintiffs, and increasingly the press now expect boards to demonstrate that AI is governed — not merely deployed. For directors, the question has shifted from 'are we using AI?' to 'can we evidence how we govern it?'",
      "We see five governance disciplines that separate boards positioned to thrive from those exposed to disclosure, regulatory, and reputational risk:",
      "1. Articulate the AI mandate. Boards must state, in writing, the company’s posture on AI — where it will lead, where it will follow, and the values that constrain its choices. The absence of such a posture is itself a finding.",
      "2. Govern the portfolio, not the project. Treat AI investment as a portfolio with capital allocation, returns reporting, and discontinuation discipline — not as an ad-hoc collection of pilots.",
      "3. Define the accountability spine. A single executive owner, an oversight committee, and a clear escalation path are non-negotiable. Distributed accountability is no accountability.",
      "4. Instrument for assurance. Move from periodic AI audits to continuous monitoring — bias, drift, cost, and exposure are all measurable.",
      "5. Cultivate AI fluency at the top. The board must be capable of reading the dashboards it is asked to approve. AI literacy programmes for directors are now table stakes.",
      "The boards that succeed will be those that treat AI with the discipline once reserved for capital allocation and risk reporting. The rest will discover — too late — that AI is a fiduciary subject whether they treated it as one or not.",
    ],
    image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1400&q=80",
    readTime: "8 min read",
    category: "AI Governance",
    date: "2025-11-18",
    author: "AlchamisGroup Partnership",
  },
  {
    id: "operations-compounding",
    title: "Compounding Operations: A Decade of Quiet Returns",
    eyebrow: "Research",
    summary:
      "Why the most resilient firms treat operations as a capital allocation discipline, not a cost line.",
    body: [
      "A decade-long study of operating performance across 240 industrial and services firms reveals a counter-intuitive truth: the firms that compounded value were not those that pursued large-scale transformation programmes, but those that treated operations as a portfolio of small, well-engineered investments — every year, every cycle.",
      "Three patterns recur in this cohort:",
      "Patience over flair. Compounders preferred quiet improvements with high return-on-effort to flagship programmes with high visibility and low realised value.",
      "Capability over consultancy. They invested in internal operating muscle — process intelligence teams, internal AI guilds, in-house automation factories — rather than depending on rotating advisory rosters.",
      "Measurement over storytelling. They built operations dashboards that resembled financial statements: clear lines, integrity, and the same scrutiny.",
      "The implication for boards is sobering. Operational excellence is not a quarterly slide; it is the most durable form of compounding capital allocation a board can sponsor. Recognising it requires the same discipline as recognising goodwill on a balance sheet.",
    ],
    image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1400&q=80",
    readTime: "12 min read",
    category: "Operations",
    date: "2025-10-04",
    author: "AlchamisGroup Research",
  },
  {
    id: "compliance-as-product",
    title: "Compliance as a Product",
    eyebrow: "Field Notes",
    summary:
      "The industrial firms outperforming peers have stopped treating compliance as overhead — and started shipping it.",
    body: [
      "In the most regulated industries, compliance has historically been treated as a cost of doing business. The leaders we have studied increasingly treat it as a product — designed, instrumented, evidenced, and continuously improved.",
      "The shift requires three discontinuities:",
      "Compliance as software. Policies embedded into systems of record, controls evidenced automatically, and audit artefacts generated as a by-product of operations rather than a parallel exercise.",
      "Compliance as platform. Investing in a compliance platform that scales across products, plants, and partners — rather than rebuilding controls per programme.",
      "Compliance as advantage. Using audit-grade evidence chains as a commercial differentiator with sophisticated clients and regulators.",
      "The firms making this shift are not paying more for compliance. They are paying differently — and converting compliance from a tax into a moat.",
    ],
    image: "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?auto=format&fit=crop&w=1400&q=80",
    readTime: "6 min read",
    category: "Compliance",
    date: "2025-09-12",
    author: "AlchamisGroup Practice",
  },
];

// ===========================================================================
// ASSESSMENT FIELDS (used by AI Advisor)
// ===========================================================================
export const ASSESSMENT_FIELDS = {
  industries: [
    "Financial Services",
    "Healthcare & Life Sciences",
    "Manufacturing",
    "Aerospace & Defense",
    "Technology",
    "Retail & Consumer",
    "Energy & Utilities",
    "Education & Childcare",
    "Hospitality",
    "Public Sector",
    "Other",
  ],
  company_sizes: [
    { value: "1-50", label: "1 — 50 employees" },
    { value: "51-200", label: "51 — 200 employees" },
    { value: "201-1000", label: "201 — 1,000 employees" },
    { value: "1001-5000", label: "1,001 — 5,000 employees" },
    { value: "5000+", label: "5,000+ employees" },
  ],
  current_systems: [
    "Legacy ERP",
    "Salesforce / HubSpot",
    "Microsoft Dynamics",
    "SAP",
    "Oracle",
    "Custom in-house tools",
    "Spreadsheets / manual",
    "No central system",
  ],
  challenges: [
    "Fragmented operations",
    "Manual / repetitive workflows",
    "Customer churn or retention",
    "Compliance & audit pressure",
    "Workforce productivity",
    "AI adoption gaps",
    "Data quality & visibility",
    "Cost-to-serve growth",
  ],
  compliance: [
    "SOC 2",
    "ISO 27001",
    "GDPR",
    "HIPAA",
    "FDA / 21 CFR Part 11",
    "Industry-specific",
    "None / unsure",
  ],
  goals: [
    "Operational excellence",
    "Revenue growth",
    "AI-led transformation",
    "International expansion",
    "Cost optimisation",
    "Investor readiness",
    "Risk reduction",
  ],
  ai_stages: [
    { value: "exploring", label: "Exploring — early conversations" },
    { value: "piloting", label: "Piloting — running first projects" },
    { value: "scaling", label: "Scaling — moving to production" },
    { value: "established", label: "Established — AI is operational" },
  ],
};

// SEO defaults used by Helmet
export const SEO_DEFAULTS = {
  siteName: "AlchamisGroup",
  baseUrl: "https://www.alchamisgroup.com",
  defaultDescription:
    "AlchamisGroup is a global consulting and technology group — operations, compliance, AI adoption, and enterprise software platforms.",
  defaultImage:
    "https://customer-assets.emergentagent.com/job_ai-adoption-platform/artifacts/022rrolx_AlchamisNew5%20%283%29.png",
  logoUrl:
    "https://customer-assets.emergentagent.com/job_ai-adoption-platform/artifacts/022rrolx_AlchamisNew5%20%283%29.png",
};
